import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MathBlock, RichText } from "../components/MathRender";
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, Save } from "lucide-react";

function Block({ block }) {
  if (block.type === "heading") return <h3 className="font-display text-xl text-[#B8860B]">{block.value}</h3>;
  if (block.type === "math") return <MathBlock>{block.value}</MathBlock>;
  return <RichText className="leading-relaxed text-slate-700">{block.value}</RichText>;
}

// Legacy string-checked exercise (hand-authored lessons)
function CheckedExercise({ ex }) {
  const { user } = useAuth();
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const check = async () => {
    if (!user) { toast.error("Sign in to attempt exercises"); return; }
    try {
      const { data } = await api.post(`/learn/exercises/${ex.id}/attempt`, { answer });
      setResult(data);
      data.correct ? toast.success("Correct!") : toast.error("Not quite — see the explanation");
    } catch { toast.error("Could not submit"); }
  };
  return (
    <div className="axiom-card p-5" data-testid={`exercise-${ex.id}`}>
      <p className="text-sm text-slate-700">{ex.prompt}</p>
      {ex.prompt_math && <div className="mt-2"><MathBlock>{ex.prompt_math}</MathBlock></div>}
      <div className="mt-3 flex gap-2">
        <input data-testid={`exercise-input-${ex.id}`} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Your answer"
          className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 outline-none focus:border-[#E61A27]" />
        <button data-testid={`exercise-submit-${ex.id}`} onClick={check} className="btn-ghost !px-4 !py-2 text-sm">Check</button>
      </div>
      {result && (
        <div className={`mt-3 flex items-start gap-2 text-sm ${result.correct ? "text-emerald-700" : "text-amber-700"}`} data-testid={`exercise-result-${ex.id}`}>
          {result.correct && <CheckCircle2 className="mt-0.5 h-4 w-4" />}<span>{result.explanation}</span>
        </div>
      )}
    </div>
  );
}

// Book exercise: free-text attempt (saved) + reveal its specific answer-guide entry
function BookExercise({ ex, guide, initialDraft, onSave }) {
  const [attempt, setAttempt] = useState(initialDraft || "");
  const [reveal, setReveal] = useState(false);
  const [status, setStatus] = useState("");
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setStatus("Saving…");
    const t = setTimeout(() => { onSave(ex.id, attempt, true); setStatus("Saved ✓"); }, 800);
    return () => clearTimeout(t);
  }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const solution = (ex.solution_blocks && ex.solution_blocks.length) ? ex.solution_blocks : guide;
  const perQuestion = !!(ex.solution_blocks && ex.solution_blocks.length);
  return (
    <div className="axiom-card p-5" data-testid={`exercise-${ex.id}`}>
      <div className="flex items-center gap-2"><span className="font-mono text-xs text-[#E61A27]">Q{ex.num}</span></div>
      <div className="mt-2 space-y-2">{ex.prompt_blocks.map((b, i) => <Block key={i} block={b} />)}</div>
      <textarea data-testid={`exercise-input-${ex.id}`} value={attempt} onChange={(e) => setAttempt(e.target.value)} rows={3} placeholder="Draft your proof / answer… (auto-saves)"
        className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#E61A27]" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button data-testid={`exercise-reveal-${ex.id}`} onClick={() => setReveal(!reveal)} className="btn-ghost !px-4 !py-2 text-sm">
          <Eye className="h-4 w-4" /> {reveal ? "Hide answer guide" : "Reveal answer guide"}
        </button>
        <span className="flex items-center gap-1 text-xs text-slate-400" data-testid={`draft-status-${ex.id}`}>{status && <><Save className="h-3.5 w-3.5" /> {status}</>}</span>
      </div>
      {reveal && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid={`exercise-guide-${ex.id}`}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">{perQuestion ? `Answer guide — Q${ex.num}` : "Chapter answer guide"}</p>
          {solution && solution.length > 0
            ? <div className="space-y-2">{solution.map((b, i) => <Block key={i} block={b} />)}</div>
            : <p className="text-sm text-slate-600">No structured answer guide was found — compare against the Worked Examples and Theorem Proof sections.</p>}
        </div>
      )}
    </div>
  );
}

export default function LessonDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [active, setActive] = useState(0);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    api.get(`/learn/lessons/${id}`).then((r) => setLesson(r.data)).catch(() => {});
    if (user) api.get("/learn/progress").then((r) => {
      setCompleted((r.data.completed_lessons || []).includes(id));
      setDrafts(r.data.exercise_drafts || {});
      const sp = (r.data.section_progress || {})[id];
      if (sp && typeof sp.last === "number") setActive(sp.last);
    }).catch(() => {});
  }, [id, user]);

  const saveDraft = useCallback(async (exId, text, silent) => {
    if (!user) { if (!silent) toast.error("Sign in to save drafts"); return; }
    try { await api.post(`/learn/exercises/${exId}/draft`, { text }); setDrafts((d) => ({ ...d, [exId]: text })); if (!silent) toast.success("Draft saved"); }
    catch { if (!silent) toast.error("Could not save draft"); }
  }, [user]);

  const recordSection = useCallback((idx) => {
    if (user) api.post(`/learn/lessons/${id}/section`, { index: idx }).catch(() => {});
  }, [id, user]);

  const go = (idx) => { setActive(idx); recordSection(idx); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const markDone = async () => {
    if (!user) { toast.error("Sign in to track progress"); return; }
    try { await api.post(`/learn/lessons/${id}/complete`); setCompleted(true); toast.success("Lesson marked complete"); }
    catch { toast.error("Could not update progress"); }
  };

  if (!lesson) return <div className="px-6 py-20 text-center text-slate-500">Loading…</div>;

  const sections = Array.isArray(lesson.sections) ? lesson.sections : [];
  const hasSections = sections.length > 0;
  const sec = hasSections ? sections[Math.min(active, sections.length - 1)] : null;
  const isProblemSet = sec && sec.type === "Problem Set" && (lesson.exercises || []).length > 0;

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="lesson-detail-page">
      <Link to="/learn" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to library
      </Link>
      <div className="flex items-center gap-2">
        <span className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-[#B8860B]">{lesson.subject || lesson.domain}</span>
        <span className="text-[11px] font-mono text-slate-400">{lesson.difficulty}</span>
      </div>
      <h1 className="mt-3 font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">{lesson.title}</h1>
      <p className="mt-2 text-slate-600">{lesson.summary}</p>

      {/* legacy hand-authored content + exercises */}
      {Array.isArray(lesson.content) && lesson.content.length > 0 && (
        <>
          <article className="mt-8 space-y-5">{lesson.content.map((b, i) => <Block key={i} block={b} />)}</article>
          {Array.isArray(lesson.exercises) && lesson.exercises.length > 0 && lesson.exercises[0].prompt !== undefined && (
            <div className="mt-10"><p className="eyebrow mb-3">Exercises</p>
              <div className="space-y-4">{lesson.exercises.map((ex) => <CheckedExercise key={ex.id} ex={ex} />)}</div></div>
          )}
        </>
      )}

      {/* imported book lesson: paginated sections */}
      {hasSections && (
        <>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="section-tabs">
            {sections.map((s, i) => (
              <button key={i} data-testid={`section-tab-${i}`} onClick={() => go(i)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${i === active ? "border-[#E61A27] bg-[#E61A27] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#D4AF37]"}`}>
                {s.type}
              </button>
            ))}
          </div>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-[#E61A27] transition-all" style={{ width: `${((active + 1) / sections.length) * 100}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-400" data-testid="section-position">Section {active + 1} of {sections.length} · {sec.type}</p>

          <section className="mt-6" data-testid={`section-${sec.type.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="eyebrow mb-3">{sec.type}</p>
            {isProblemSet ? (
              <div className="space-y-4" data-testid="book-exercises">
                {lesson.exercises.map((ex) => <BookExercise key={ex.id} ex={ex} guide={lesson.answer_guide_blocks} initialDraft={drafts[ex.id]} onSave={saveDraft} />)}
              </div>
            ) : (
              <article className="space-y-4">{sec.blocks.map((b, i) => <Block key={i} block={b} />)}</article>
            )}
          </section>

          <div className="mt-8 flex items-center justify-between">
            <button data-testid="section-prev" disabled={active === 0} onClick={() => go(active - 1)} className="btn-ghost !py-2 text-sm disabled:opacity-40">
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>
            {active < sections.length - 1 ? (
              <button data-testid="section-next" onClick={() => go(active + 1)} className="btn-crimson !py-2 text-sm">Next <ArrowRight className="h-4 w-4" /></button>
            ) : (
              <button data-testid="lesson-complete-btn" onClick={markDone} disabled={completed} className={`${completed ? "btn-ghost" : "btn-crimson"} !py-2 text-sm`}>
                {completed ? "Completed ✓" : "Mark lesson complete"}
              </button>
            )}
          </div>
        </>
      )}

      {!hasSections && (
        <button data-testid="lesson-complete-btn" onClick={markDone} disabled={completed} className={`mt-10 ${completed ? "btn-ghost" : "btn-crimson"}`}>
          {completed ? "Completed ✓" : "Mark lesson complete"}
        </button>
      )}
    </div>
  );
}
